import React, { useCallback, useEffect, useState } from 'react';
import api from '../../utils/api';
import {
  Card,
  EmptyState,
  FilterBar,
  FilterSelect,
  InlineStats,
  Pagination,
  StatusPill,
  TableSkeleton,
  TableWrap,
  Td,
  Th,
  Tr
} from '../../components/admin/ui';
import { useToast } from '../../components/admin/overlays';
import { caseStatusLabels, caseStatusTone, formatDate, formatNumber } from '../../components/admin/labels';
import { IconCases } from '../../components/admin/icons';
import { UpgradeGate, WindowPicker } from '../../components/courier/ui';
import { useCourierSession } from '../../components/courier/CourierLayout';

const ISSUE_OPTIONS = [
  { value: 'not_arrived', label: "Hasn't arrived yet" },
  { value: 'delivered_not_received', label: 'Delivered, not received' },
  { value: 'damaged', label: 'Damaged' },
  { value: 'missing_items', label: 'Items missing' },
  { value: 'unsafe_place', label: 'Left in unsafe place' }
];

const PER_PAGE = 25;

/**
 * Every report naming this carrier.
 *
 * Deliberately not the full case: a courier sees what was reported against
 * their network, never the customer's name, address or contact details, and
 * postcodes only as outward codes. Those belong to the customer and the
 * retailer handling it.
 */
const CourierCases = () => {
  const toast = useToast();
  const { me, fullAccess } = useCourierSession() || {};

  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [issueType, setIssueType] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!fullAccess) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams({ days, page, limit: PER_PAGE });
      if (issueType) params.append('issueType', issueType);
      const { data: payload } = await api.get(`/api/courier-portal/cases?${params}`);
      setData(payload);
    } catch (error) {
      if (error.response?.data?.code !== 'UPGRADE_REQUIRED') {
        toast('Could not load your reports.', { tone: 'error' });
      }
    } finally {
      setLoading(false);
    }
  }, [days, page, issueType, fullAccess, toast]);

  useEffect(() => {
    load();
  }, [load]);

  if (!fullAccess) {
    return (
      <UpgradeGate
        title="See every report naming your network"
        description="The pilot shows the headline figures. Intelligence opens the reports behind them — what was reported, which retailer, which postcode area and when."
        features={me?.pricing?.courierPlanPrices?.intelligence?.features || []}
        price={me?.pricing?.courierPlanPrices?.intelligence?.price ?? null}
      />
    );
  }

  return (
    <Card>
      <FilterBar
        actions={
          <>
            <InlineStats
              items={[{ label: 'Reports', value: loading ? '—' : formatNumber(data?.total || 0) }]}
            />
            <WindowPicker
              days={days}
              onChange={(d) => {
                setDays(d);
                setPage(1);
              }}
              onRefresh={load}
              loading={loading}
            />
          </>
        }
      >
        <FilterSelect
          label="Issue"
          value={issueType}
          onChange={(e) => {
            setIssueType(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All</option>
          {ISSUE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </FilterSelect>
      </FilterBar>

      {loading ? (
        <TableSkeleton rows={8} cols={5} />
      ) : !data?.cases?.length ? (
        <EmptyState
          icon={IconCases}
          title={issueType ? 'No reports match that issue type' : 'No reports in this period'}
          description={
            issueType
              ? 'Try a different issue type, or widen the time window.'
              : `Nothing has been reported against your network in the last ${days} days.`
          }
        />
      ) : (
        <TableWrap>
          <thead>
            <tr>
              <Th>Case</Th>
              <Th>Issue</Th>
              <Th>Retailer</Th>
              <Th>Area</Th>
              <Th>Status</Th>
              <Th>Reported</Th>
            </tr>
          </thead>
          <tbody>
            {data.cases.map((c) => (
              <Tr key={c.caseId}>
                <Td className="whitespace-nowrap font-600 text-ink-900">{c.caseId}</Td>
                <Td className="text-ink-700">{c.issueLabel}</Td>
                <Td className="text-ink-700">{c.merchantName || '—'}</Td>
                <Td className="font-600 tabular-nums text-ink-700">{c.area || '—'}</Td>
                <Td>
                  <StatusPill tone={caseStatusTone[c.status] || 'neutral'}>
                    {caseStatusLabels[c.status] || c.status}
                  </StatusPill>
                </Td>
                <Td className="whitespace-nowrap text-ink-500">{formatDate(c.submittedAt)}</Td>
              </Tr>
            ))}
          </tbody>
        </TableWrap>
      )}

      {!loading && data && (
        <Pagination
          page={page}
          totalPages={data.totalPages}
          total={data.total}
          perPage={PER_PAGE}
          shown={data.cases?.length || 0}
          onPage={setPage}
        />
      )}
    </Card>
  );
};

export default CourierCases;
